import { matchPath } from 'react-router'
import { ATLAS_AI_NOTE_WORKING } from '@common/constants/webSockets'
import { IFlagData } from '@common/interfaces/bbc'
import { IResponse } from '@common/interfaces/request'
import { Action } from 'redux'
import { takeEvery, select } from 'redux-saga/effects'
import { ROUTES } from '../../constants/routes'
import { currentWorkFlow } from '../../helpers/helpers'
import { putPromise } from '../../helpers/redux'
import { IArcNoteWorkingAction } from '../types'
import {
  CALCULATE_BBC_FLAGS_SUCCESS,
  CALCULATE_ENTITY_FLAGS_SUCCESS,
  CALCULATE_OPS_FLAGS_SUCCESS,
  CALCULATE_ONGOING_REPORTING_FLAGS_SUCCESS,
  getNewFlagsCount,
  listFlags,
} from './actions'
import { flags as flagsSelector, lastListContext as lastListContextSelector } from './selectors'

export function* repullNewFlagCount() {
  yield takeEvery(
    [
      CALCULATE_BBC_FLAGS_SUCCESS,
      CALCULATE_ENTITY_FLAGS_SUCCESS,
      CALCULATE_OPS_FLAGS_SUCCESS,
      CALCULATE_ONGOING_REPORTING_FLAGS_SUCCESS,
    ],
    function* (action: IResponse & Action) {
      const actionData = action.data as { id: string }
      const path = window.location.pathname
      const bbcPath = matchPath(`${ROUTES.BBC_SUMMARY}/*`, path)
      const entityPath = matchPath(ROUTES.ENTITY_PAGE, path)
      const opsPath = matchPath(ROUTES.PROSPECT_PAGE, path)
      const ongoingPath = matchPath(ROUTES.ONGOING_REPORTING, path)

      const matchedPath = bbcPath || entityPath || opsPath || ongoingPath

      if (!matchedPath) {
        return
      }
      const {
        params: { id: matchedId },
      } = matchedPath

      const workflow = currentWorkFlow(path)
      const requestId = actionData.id

      if (matchedId === requestId) {
        yield putPromise(
          getNewFlagsCount({
            id: requestId,
            workflow,
          }),
        )
      }
    },
  )
}

// When Arc finishes working a flag comment thread it was @-tagged in
// (ATLAS_AI_NOTE_WORKING → isArcWorking:false), silently refetch the loaded
// flags list so Arc's freshly-posted reply appears without a manual reload —
// the flag reducer's live isArcWorking update only hides the "working"
// indicator, it can't fetch the new comment. Mirrors notes' refetchNotesOnArcComplete.
//
// Reuses `lastListContext` (params of the most recent non-loadMore fetch) so
// the same page reloads; `skipLoader` avoids a skeleton flash. Only fires when
// the completed thread's flag is actually in this user's loaded list, since the
// websocket fans out to every internal user.
export function* refetchFlagsOnArcComplete() {
  yield takeEvery(ATLAS_AI_NOTE_WORKING, function* (action: IArcNoteWorkingAction) {
    if (action.data?.isArcWorking) {
      return
    }
    const loadedFlags = (yield select(flagsSelector)) as IFlagData | null
    const ctx = (yield select(lastListContextSelector)) as Record<string, unknown> | null
    if (!loadedFlags?.data?.length || !ctx) {
      return
    }
    if (!loadedFlags.data.some((flag) => flag.notes?.id === action.data.noteId)) {
      return
    }
    yield putPromise(listFlags({ ...ctx, loadMore: false, skipLoader: true }))
  })
}
